import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Modal, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Helmet } from 'react-helmet-async';

interface GalleryImage {
  id: number;
  url: string;
}

const Gallery: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [displayCount, setDisplayCount] = useState(9);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/bookings?action=gallery');
        if (!res.ok) throw new Error('Gagal memuat data gallery');
        const data = await res.json();
        setGalleryImages(data.reverse());
        setError(null);
      } catch (err) {
        setGalleryImages([]);
        setError('Gagal memuat galeri.');
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const sortedImages = galleryImages;
  const displayedImages = sortedImages.slice(0, displayCount);

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage('');
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 9);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Helmet>
        <title>Gallery - RaketerS</title>
      </Helmet>
      <Typography variant="h4" component="h1" gutterBottom>
        Gallery
      </Typography>
      {loading ? (
        <Typography variant="h6">Memuat gallery...</Typography>
      ) : error ? (
        <Typography color="error" variant="body1">{error}</Typography>
      ) : galleryImages.length === 0 ? (
        <Typography variant="body1">Belum ada gambar gallery.</Typography>
      ) : (
        <Grid container spacing={3}>
          {displayedImages.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image.id}>
              <Box
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
                onClick={() => handleImageClick(image.url)}
              >
                <img
                  src={image.url}
                  alt={`Gallery ${image.id}`}
                  style={{
                    width: '100%',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {displayCount < galleryImages.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            onClick={handleLoadMore}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Tampilkan Lebih Banyak
          </Button>
        </Box>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            outline: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 1
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: 'white',
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Selected gallery"
            style={{
              maxHeight: '80vh',
              maxWidth: '80vw',
              borderRadius: 8,
              display: 'block',
              margin: 'auto'
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default Gallery; 